import React from 'react';

/**
 * Table 컴포넌트
 *
 * @param {Array} columns - 컬럼 정의 [{key, label, width, align, render}]
 * @param {Array} data - 테이블 데이터
 * @param {function} onRowClick - 행 클릭 핸들러
 * @param {boolean} loading - 로딩 상태
 * @param {string} emptyMessage - 데이터 없을 때 메시지
 * @param {boolean} striped - 줄무늬 스타일
 * @param {boolean} hoverable - 호버 효과
 */
const Table = ({
  columns = [],
  data = [],
  onRowClick,
  loading = false,
  emptyMessage = '데이터가 없습니다.',
  striped = false,
  hoverable = true
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 gap-3 text-gray-500 text-sm">
        <div className="w-7 h-7 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <span>로딩 중...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 gap-2">
        <span className="text-[32px]">📭</span>
        <span className="text-sm text-gray-500">{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  width: col.width,
                  textAlign: col.align || 'left'
                }}
                className="px-4 py-3 bg-gray-50 text-gray-500 font-medium text-xs uppercase tracking-wide whitespace-nowrap border-b border-gray-200"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={row.id || rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              className={`
                ${striped && rowIndex % 2 === 1 ? 'bg-gray-50' : ''}
                ${hoverable ? 'hover:bg-gray-100' : ''}
                ${onRowClick ? 'cursor-pointer hover:bg-blue-50' : ''}
                ${rowIndex === data.length - 1 ? '' : 'border-b border-gray-200'}
              `}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{ textAlign: col.align || 'left' }}
                  className="px-4 py-3 text-gray-900 align-middle"
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Pagination 컴포넌트
 *
 * @param {number} currentPage - 현재 페이지
 * @param {number} totalPages - 전체 페이지 수
 * @param {function} onPageChange - 페이지 변경 핸들러
 */
export const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange
}) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  const buttonBase = "flex items-center justify-center min-w-[32px] h-8 px-2.5 bg-white border border-gray-200 rounded-md text-gray-500 text-[13px] font-medium cursor-pointer transition-all duration-150";
  const buttonHover = "hover:bg-gray-100 hover:border-gray-300";
  const buttonActive = "bg-gray-900 border-gray-900 text-white";
  const buttonDisabled = "opacity-50 cursor-not-allowed";

  return (
    <div className="flex items-center justify-center gap-1 p-4">
      <button
        className={`${buttonBase} ${currentPage === 1 ? buttonDisabled : buttonHover}`}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ‹
      </button>

      {start > 1 && (
        <>
          <button
            className={`${buttonBase} ${buttonHover}`}
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          {start > 2 && <span className="px-1 text-gray-400">...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          className={`${buttonBase} ${page === currentPage ? buttonActive : buttonHover}`}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="px-1 text-gray-400">...</span>}
          <button
            className={`${buttonBase} ${buttonHover}`}
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        className={`${buttonBase} ${currentPage === totalPages ? buttonDisabled : buttonHover}`}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        ›
      </button>
    </div>
  );
};

export default Table;
